import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { Form } from '@edx/paragon';
import { ReactComponent as RadioIcon } from '../../profile/assets/radio-button.svg';
import { ReactComponent as RadioEmptyIcon } from '../../profile/assets/radio-button-empty.svg';
import { years } from './constants';
import messages from './YearOfBirth.messages';

// Components
import FormControls from './elements/FormControls';
import EditableItemHeader from './elements/EditableItemHeader';
import EmptyContent from './elements/EmptyContent';
import SwitchContent from './elements/SwitchContent';
import CustomSelect from '../components/CustomSelect/CustomSelect';

// Selectors
import { yearOfBirthSelector } from '../data/selectors';

const YearOfBirth = ({
  formId,
  yearOfBirth,
  visibilityYearOfBirth,
  saveState,
  error,
  intl,
  changeHandler,
  submitHandler,
  closeHandler,
  openHandler,
  changeData,
  saveData,
  onSaveComplete,
  editMode: initialEditMode,
}) => {
  const [editMode, setEditMode] = useState(changeData ? 'editing' : initialEditMode);
  const [selectedYear, setSelectedYear] = useState(yearOfBirth || '');

  useEffect(() => {
    setSelectedYear(yearOfBirth || '');
  }, [yearOfBirth]);

  useEffect(() => {
    if (changeData) {
      setEditMode('editing');
    } else {
      handleClose();
    }
  }, [changeData]);

  useEffect(() => {
    if (saveData) {
      handleSubmit();
    }
  }, [saveData]);

  const handleChange = value => {
    setSelectedYear(value);
    // Convert empty string to null for yearOfBirth
    const processedValue = value === '' ? null : parseInt(value, 10);
    changeHandler(formId, processedValue);
  };

  const handleSubmit = e => {
    // Проверяем, есть ли объект события
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    submitHandler(formId);
    if (onSaveComplete) {
      onSaveComplete(); // Сбрасываем состояние saveData в родительском компоненте
    }
  };

  const handleClose = () => {
    closeHandler(formId);
    setEditMode('static');
  };

  const handleOpen = () => {
    openHandler(formId);
    setEditMode('editing');
  };

  const yearOptions = [
    {
      value: '',
      label: '——',
      displayLabel: intl.formatMessage(messages['profile.yearOfBirth.option.default']),
    },
    ...years.map(year => ({
      value: String(year),
      label: String(year),
      displayLabel: String(year),
    })),
  ];

  const renderOption = (option, currentValue) => {
    const isSelected = option.value === String(currentValue);
    const Icon = isSelected ? RadioIcon : RadioEmptyIcon;

    return (
      <>
        <Icon className="tw-w-4 tw-h-4 tw-min-w-4 tw-min-h-4" />
        <span className={option.value === '' ? 'tw-text-neutral-500' : ''}>{option.label}</span>
      </>
    );
  };

  return (
    <SwitchContent
      className="tw-mb-10"
      expression={editMode}
      cases={{
        editing: (
          <div role="dialog" aria-labelledby={`${formId}-label`}>
            <form onSubmit={handleSubmit}>
              <Form.Group controlId={formId} isInvalid={error !== null} className="tw-mb-0">
                <p className="!tw-mb-2 tw-text-[16px] tw-text-neutral-1000 tw-font-medium tw-leading-[100%]">
                  {intl.formatMessage(messages['profile.yearOfBirth.label'])}
                </p>
                <p className="tw-text-[14px] tw-text-placeholder tw-font-medium !tw-mb-[14px] tw-leading-[120%]">
                  {intl.formatMessage(messages['profile.yearOfBirth.placeholder'])}
                </p>
                <CustomSelect
                  options={yearOptions}
                  value={String(selectedYear)}
                  onChange={handleChange}
                  placeholder={intl.formatMessage(messages['profile.yearOfBirth.option.default'])}
                  renderOption={renderOption}
                  getOptionLabel={option => option.displayLabel}
                  getOptionValue={option => option.value}
                />
                {error !== null && (
                  <Form.Control.Feedback hasIcon={false}>{error}</Form.Control.Feedback>
                )}
              </Form.Group>
              <div className="tw-hidden">
                <FormControls
                  visibilityId="visibilityYearOfBirth"
                  saveState={saveState}
                  visibility={visibilityYearOfBirth}
                  cancelHandler={handleClose}
                  changeHandler={handleChange}
                  saveData={saveData}
                />
              </div>
            </form>
          </div>
        ),
        editable: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.yearOfBirth.label'])}
              showEditButton
              onClickEdit={handleOpen}
              showVisibility={visibilityYearOfBirth !== null}
              visibility={visibilityYearOfBirth}
            />
            <p data-hj-suppress className="h5">
              {yearOfBirth}
            </p>
          </>
        ),
        empty: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.yearOfBirth.label'])}
            />
            <EmptyContent onClick={handleOpen}>
              {intl.formatMessage(messages['profile.yearOfBirth.empty'])}
            </EmptyContent>
          </>
        ),
        static: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.yearOfBirth.label'])}
            />
            <p
              data-hj-suppress
              className="tw-text-[16px] tw-text-neutral-1000 tw-font-medium tw-leading-[150%]"
            >
              {yearOfBirth || (
                <span className="tw-text-[14px] tw-text-placeholder tw-font-medium">
                  {intl.formatMessage(messages['profile.yearOfBirth.placeholder'])}
                </span>
              )}
            </p>
          </>
        ),
      }}
    />
  );
};

YearOfBirth.propTypes = {
  formId: PropTypes.string.isRequired,
  yearOfBirth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  visibilityYearOfBirth: PropTypes.oneOf(['private', 'all_users']),
  editMode: PropTypes.oneOf(['editing', 'editable', 'empty', 'static']),
  saveState: PropTypes.string,
  error: PropTypes.string,
  changeHandler: PropTypes.func.isRequired,
  submitHandler: PropTypes.func.isRequired,
  closeHandler: PropTypes.func.isRequired,
  openHandler: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  changeData: PropTypes.bool,
  saveData: PropTypes.bool,
  onSaveComplete: PropTypes.func,
};

YearOfBirth.defaultProps = {
  editMode: 'static',
  saveState: null,
  yearOfBirth: null,
  visibilityYearOfBirth: 'private',
  error: null,
  changeData: false,
  saveData: false,
  onSaveComplete: null,
};

export default connect(yearOfBirthSelector, {})(injectIntl(YearOfBirth));
